'use client'

import Image from 'next/image'
import Link from 'next/link'
import { getMediaUrl } from 'src/_helpers/client/getMediaUrl'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { GlobalContext } from 'src/components/layout/GlobalContext'
import { useContext } from 'react'
//import LINKS from 'src/_helpers/links'
//import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { getDomainJournalFooterColumns } from './domainJournalLinks'
import useJournalDomainRoutes from './useJournalDomainRoutes'
import LocationIcon from 'public/icons/interface/location.svg'
import EmailIcon from 'public/icons/interface/e-mail.svg'
import PhoneIcon from 'public/icons/interface/phone.svg'
//import ArrowIcon from 'public/icons/arrows/arrow-circle-skew2.svg'
//import UniversityLogo from 'public/icons/media/logo.svg'
import FacebookIcon from 'public/icons/social-media/facebook.svg'
import InstagramIcon from 'public/icons/social-media/instagram.svg'
import LinkedinIcon from 'public/icons/social-media/linkedin.svg'
import YoutubeIcon from 'public/icons/social-media/youtube.svg'
import XIcon from 'public/icons/social-media/x.svg'

//const UNIVERSITY_URL = 'https://www.uw.edu.pl/'

const SOCIAL_LINKS = [
  ['linkFacebook', 'facebook', FacebookIcon],
  ['linkInstagram', 'instagram', InstagramIcon],
  ['linkedin', 'linkedin', LinkedinIcon],
  ['linkYoutube', 'youtube', YoutubeIcon],
  ['linkTwitter', 'x', XIcon],
]

function LogoImage({ media, alt, className }) {
  if (!media?.url) {
    return null
  }

  return (
    <Image
      src={getMediaUrl(media.url, media.version)}
      alt={alt || ''}
      width={media.width || 160}
      height={media.height || 80}
      className={`h-auto w-auto object-contain ${className || ''}`}
    />
  )
}

export function DomainSiteFooter({
  journal,
  locale,
  config = null,
  logos = null,
  columns = null,
  primaryColor = null,
  contactTitle = null,
}) {
  const translate = useTranslateEntry()
  const { openCookiesModal } = useContext(GlobalContext)
  const journalRoutes = useJournalDomainRoutes()
  const translation = getPreferredTranslation(journal, locale) || {}
  const title = translation.title || journal.title
  //const slug = translation.slug
  const linkColumns = columns || getDomainJournalFooterColumns(journal, locale, translate, journalRoutes)
  const resolvedLogos = Array.isArray(logos) ? logos.filter(Boolean) : [journal?.logo1, journal?.logo2].filter(Boolean)
  const address = translation.contactAddress || journal.contactAddress || journal.address
  const email = translation.contactEmail || journal.contactEmail || journal.email
  const phone = translation.contactPhone || journal.contactPhone || journal.phone
  //const mainUrl = process.env.NEXT_PUBLIC_MAIN_URL?.replace(/\/$/, '') || ''
  //const journalPlatformPath = slug ? buildLocalizedPath(LINKS.JOURNALS.DETAIL, locale, { slug }) : null
  //const journalPlatformUrl = journalPlatformPath ? `${mainUrl}${journalPlatformPath}` : null

  return (
    <footer>
      <div
        className="bg-basic-gray1 px-16 py-45 text-basic-white d:px-40 d:py-88"
        style={primaryColor ? { backgroundColor: primaryColor } : undefined}
      >
        <div className="mx-auto grid w-full max-w-screen-2xl gap-40 d:grid-cols-[1fr_2fr]">
          <section className="flex flex-col gap-24">
            <h2 className="h1 h1--bold text-basic-white">{contactTitle || translate('T_JOURNAL_CONTACT')}</h2>
            <address className="flex flex-col gap-8 not-italic">
              {address && (
                <p className="body-b3-normal flex gap-6 text-basic-white">
                  <LocationIcon className="mt-1 size-21 shrink-0 [&_path]:stroke-basic-white" />
                  <span>{address}</span>
                </p>
              )}
              {email && (
                <p className="body-b3-normal flex gap-6 text-basic-white">
                  <EmailIcon className="mt-1 size-21 shrink-0 [&_path]:stroke-basic-white" />
                  <a className="underline" href={`mailto:${email}`}>
                    {email}
                  </a>
                </p>
              )}
              {phone && (
                <p className="body-b3-normal flex gap-6 text-basic-white">
                  <PhoneIcon className="mt-1 size-21 shrink-0 [&_path]:stroke-basic-white" />
                  <span>{phone}</span>
                </p>
              )}
            </address>
          </section>

          <nav className="grid gap-24 border-basic-gray3 d:grid-cols-2 d:border-l d:pl-80" aria-label="Footer">
            {linkColumns.map((links, columnIndex) => (
              <div key={columnIndex} className="flex flex-col gap-24">
                {links.map(([label, href]) => (
                  <Link
                    key={`${href}-${label}`}
                    href={href}
                    className="buttons-l relative block w-fit text-basic-white after:absolute after:bottom-0 after:left-0 after:h-1 after:w-0 after:bg-basic-white after:transition-[width] supports-hover:hover:after:w-full"
                  >
                    {label}
                  </Link>
                ))}
                {columnIndex === linkColumns.length - 1 && (
                  <button
                    type="button"
                    onClick={openCookiesModal}
                    aria-haspopup="dialog"
                    className="buttons-l relative block w-fit text-basic-white after:absolute after:bottom-0 after:left-0 after:h-1 after:w-0 after:bg-basic-white after:transition-[width] supports-hover:hover:after:w-full"
                  >
                    Cookies
                  </button>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      <div className="px-16 py-32 d:px-40">
        <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-28 d:flex-row d:items-center d:justify-between">
          <div className="flex flex-col gap-20">
            <div className="flex flex-wrap items-center gap-24">
              {resolvedLogos.length > 0 ? (
                resolvedLogos.map((media, index) => (
                  <LogoImage
                    key={media?.uuid || media?.url || index}
                    media={media}
                    alt={title}
                    className={index === 0 ? 'max-h-76' : 'max-h-61'}
                  />
                ))
              ) : (
                <span className="h3-bold text-basic-black">{title}</span>
              )}
            </div>

            <div className="flex items-center gap-16">
              {SOCIAL_LINKS.map(([field, key, Icon]) => {
                const href = journal?.[field] || config?.[field]

                return href ? (
                  <Link
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={key}
                    className="flex size-24 items-center justify-center text-basic-black transition-opacity supports-hover:hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-functional-focus-color"
                  >
                    <Icon className="h-auto w-auto shrink-0" />
                  </Link>
                ) : null
              })}
            </div>
          </div>

          <div className="flex flex-col gap-20 d:items-end">
            <div className="flex flex-col gap-16 d:flex-row d:items-center d:gap-24">
              {/*
              <Link
                href={UNIVERSITY_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="University of Warsaw"
                className="flex h-48 w-fit items-center gap-14 d:h-64"
              >
                <UniversityLogo className="h-full w-auto" viewBox="0 0 206 84" />
              </Link>

              {journalPlatformUrl && (
                <Link
                  href={journalPlatformUrl}
                  className="group buttons-m buttons--bold flex max-w-332 items-center gap-8 text-basic-black"
                >
                  <span className="relative after:absolute after:bottom-0 after:left-0 after:h-1 after:w-0 after:bg-basic-black after:transition-[width] supports-hover:group-hover:after:w-full">
                    See the journal on University-wide Publishing Platform
                  </span>
                  <ArrowIcon className="size-21 shrink-0" />
                </Link>
              )}
              */}
            </div>

            <p className="body-b4 text-basic-black">Copyright All Rights Reserved © {new Date().getFullYear()}</p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default function DomainJournalFooter(props) {
  return <DomainSiteFooter {...props} />
}
